import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("ost_token");
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ---------------------------------------------------------------------------
// Silent token refresh on 401. Uses the existing httpOnly refresh_token cookie
// (7-day lifetime). Retries the original request once after a successful
// refresh. If refresh also fails, the original error bubbles up so callers
// (e.g. ProductForm) can decide what to do without us yanking the user away
// from the page they're working on.
// ---------------------------------------------------------------------------
let refreshInFlight = null;
api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        if (status !== 401 || !original || original._retry) {
            return Promise.reject(error);
        }
        // Don't try to refresh the refresh call itself or the login call.
        const url = original.url || "";
        if (url.includes("/auth/refresh") || url.includes("/auth/login")) {
            return Promise.reject(error);
        }
        original._retry = true;
        try {
            if (!refreshInFlight) {
                refreshInFlight = api.post("/auth/refresh").finally(() => {
                    // allow next 401 in the future
                    setTimeout(() => { refreshInFlight = null; }, 0);
                });
            }
            const { data } = await refreshInFlight;
            if (data?.access_token) {
                localStorage.setItem("ost_token", data.access_token);
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${data.access_token}`;
            }
            return api(original);
        } catch (e) {
            return Promise.reject(error);
        }
    },
);

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function imageUrl(path, variant) {
    if (!path) return "";
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const base = `${BACKEND_URL}${path}`;
    // `variant` is opt-in: backend returns the original when missing or unknown.
    // Only append for our own /api/images/<id> paths to avoid polluting other URLs.
    if (variant && path.includes("/api/images/")) {
        const sep = base.includes("?") ? "&" : "?";
        return `${base}${sep}v=${encodeURIComponent(variant)}`;
    }
    return base;
}

export function formatPrice(value, currency = "Rs.") {
    const n = Number(value || 0);
    return `${currency} ${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
